/**
 * Sentry event filters — pure, so they can be node-tested without the SDK.
 *
 * Two classes of event reach production Sentry without describing a defect.
 * Both were confirmed against live issues in `javascript-react` (2026-09-14):
 *
 * 1. **Stale-chunk recovery fallout** (JAVASCRIPT-REACT-8 / -B, 44 events).
 *    `main.tsx` calls `preventDefault()` on `vite:preloadError` so its own
 *    reload supersedes the throw. Vite's preload helper is
 *    `baseModule().catch(handlePreloadError)` and `handlePreloadError`
 *    rethrows only `if (!e.defaultPrevented)` — so suppressing the throw
 *    *resolves the dynamic import with `undefined`*. `React.lazy` then stores
 *    `_status = Resolved, _result = undefined` and the next render reads
 *    `payload._result.default` off `undefined`. `location.reload()` is async,
 *    so React keeps rendering into the ErrorBoundary before the navigation
 *    commits. The recovery works; only the report is spurious.
 *
 * 2. **IndexedDB unavailability** (JAVASCRIPT-REACT-9 / -D, 7 events).
 *    Firebase Auth's `indexedDBLocalPersistence` and App Check's token cache
 *    both use IndexedDB; Firestore does not (we run a memory cache). When the
 *    browser denies the database — private-mode Safari, evicted storage, a
 *    visitor clearing data mid-session — Firebase degrades to in-memory and
 *    the page keeps working, but the rejection reaches `onunhandledrejection`
 *    with no stack. Environment, not defect.
 *
 * Anything not matched here still reports. These filters are deliberately
 * narrow: they name observed signatures rather than whole error families, so
 * a real bug in the same area is never swallowed.
 */

/** The subset of a Sentry event these filters inspect. */
export interface FilterableEvent {
  exception?: { values?: Array<{ type?: string; value?: string }> };
  message?: string;
}

export interface DropContext {
  /** True once `main.tsx` has committed to a stale-chunk recovery reload. */
  chunkReloadInFlight: boolean;
}

/**
 * Browser signatures meaning "IndexedDB is not usable here". Add a pattern
 * only after seeing it in a live issue — a broad `/indexeddb/i` would hide
 * genuine bugs in our own storage code.
 */
const INDEXED_DB_UNAVAILABLE: readonly RegExp[] = [
  /connection to indexed database server lost/i,
  /object store cannot be found in the database/i,
];

/** Every human-readable string on the event, for signature matching. */
function messagesOf(event: FilterableEvent): string[] {
  const out: string[] = [];
  if (event.message) out.push(event.message);
  for (const value of event.exception?.values ?? []) {
    if (value?.value) out.push(value.value);
  }
  return out;
}

/** True when the event is a browser refusing IndexedDB rather than a defect. */
export function isIndexedDbUnavailable(event: FilterableEvent): boolean {
  const messages = messagesOf(event);
  return INDEXED_DB_UNAVAILABLE.some((pattern) => messages.some((m) => pattern.test(m)));
}

/**
 * Whether this event should be dropped instead of sent.
 *
 * During a stale-chunk reload every error is a casualty of the teardown, so
 * the whole window is suppressed rather than one signature — the tab is
 * navigating away regardless. Outside that window the identical TypeError is
 * a real bug and still reports, as does the un-suppressed
 * "Failed to fetch dynamically imported module" from the rate-limited path.
 */
export function shouldDropEvent(event: FilterableEvent, ctx: DropContext): boolean {
  if (ctx.chunkReloadInFlight) return true;
  return isIndexedDbUnavailable(event);
}
